// src/pages/Courses.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL!;

interface APICourse {
  id: number;
  teacher: {
    user: {
      first_name: string;
      last_name: string;
    };
  };
  class_for: { id: number; name: string }[];
  tags: { id: number; name: string }[];
  course_img: string;
  course_name: string;
  description: string;
  is_completed: boolean;
  mode: string;
  price: string;               // e.g. "1999.00"
  discount_percentage: string; // e.g. "90.00"
}

interface Course {
  id: number;
  title: string;
  imageUrl: string;
  instructor: string;
  subject: string;
  examFocus: string;
  price: number;
  discountPrice: number;
  rating: number;
  reviewCount: number;
  problems: number;
  difficulty: string;
  badges: string[];
}

interface Category {
  id: string;
  name: string;
}

const CoursesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [coursesByCategory, setCoursesByCategory] = useState<Record<string, Course[]>>({});
  const [activeCategory, setActiveCategory] = useState<string>('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const resp = await axios.get<Record<string, APICourse[]>>(
          `${API_URL}/courses/classwise/`
        );
        const data = resp.data;

        // build category list
        const cats: Category[] = Object.keys(data).map((key) => ({
          id: key,
          name: formatCategoryName(key),
        }));

        // map each API course to our Course type
        const mapped: Record<string, Course[]> = {};
        for (const [catId, list] of Object.entries(data)) {
          mapped[catId] = list.map((c) => {
            const priceNum = parseFloat(c.price);
            const discPct = parseFloat(c.discount_percentage);
            const discountPrice = Math.round(priceNum * (100 - discPct) / 100);
            return {
              id: c.id,
              title: c.course_name,
              imageUrl: c.course_img,
              instructor: `${c.teacher.user.first_name} ${c.teacher.user.last_name}`,
              subject: c.tags[0]?.name || 'General',
              examFocus: c.description,
              price: priceNum,
              discountPrice,
              rating: 0,        // default
              reviewCount: 0,   // default
              problems: 0,      // default
              difficulty: 'Standard', // default
              badges: c.tags.map((t) => t.name),
            };
          });
        }

        setCategories(cats);
        setCoursesByCategory(mapped);

        if (cats.length) {
          setActiveCategory(cats[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch courses', err);
      }
    };
    fetchCourses();
  }, []);

  // e.g. "Arts11" → "Class 11 Arts", "Neet12" → "NEET Class 12"
  const formatCategoryName = (catId: string): string => {
    const m = catId.match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return catId;
    let [_, label, num] = m;
    label = label.toLowerCase();
    if (label === 'neet') {
      return `NEET Class ${num}`;
    }
    return `Class ${num} ${label.charAt(0).toUpperCase() + label.slice(1)}`;
  };

  const getSubjectGradient = (subject: string): string => {
    const map: Record<string,string> = {
      Physics: 'from-blue-600 to-indigo-700',
      Chemistry: 'from-green-600 to-emerald-700',
      Biology: 'from-teal-600 to-green-700',
      Mathematics: 'from-purple-600 to-indigo-700',
      History: 'from-amber-600 to-orange-700',
      'Political Science': 'from-red-600 to-rose-700',
      Economics: 'from-cyan-600 to-blue-700',
      Science: 'from-blue-600 to-teal-700',
      'Social Studies': 'from-amber-600 to-yellow-700',
      English: 'from-indigo-600 to-violet-700',
    };
    return map[subject] ?? 'from-blue-600 to-violet-700';
  };

  const renderStars = (rating: number) => (
    <div className="flex">
      {[1,2,3,4,5].map((n) => (
        <span
          key={n}
          className={n <= Math.round(rating) ? 'text-yellow-400' : 'text-gray-300'}
        >
          ★
        </span>
      ))}
    </div>
  );

  const getDiscountPercentage = (price: number, discountPrice: number): number =>
    Math.round(((price - discountPrice) / price) * 100);

  // ─── Course Card ───────────────────────────────────────────────
  const ProblemCourseCard: React.FC<{ course: Course }> = ({ course }) => {
    const [hover, setHover] = useState(false);
    const gradient = getSubjectGradient(course.subject);
    const discountPct = getDiscountPercentage(course.price, course.discountPrice);

    return (
      <div
        className="w-72 bg-white rounded-xl shadow-lg overflow-hidden relative transition-transform"
        style={{ transform: hover ? 'translateY(-8px)' : 'none' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className={`relative h-40 bg-gradient-to-r ${gradient}`}>
          <img
            src={course.imageUrl}
            alt={course.title}
            className="w-full h-full"
          />
          <div className="absolute top-3 left-3 flex gap-2">
            {course.badges.map((b, i) => (
              <span
                key={i}
                className="px-2 py-1 bg-white/90 text-xs font-bold rounded-full shadow-sm text-purple-800"
              >
                {b}
              </span>
            ))}
          </div>
          <div className="absolute bottom-3 right-3">
            <span className="px-3 py-1 bg-black/70 text-white text-sm font-bold rounded-lg">
              1500+ Problems
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="text-lg font-bold text-gray-800 h-14 overflow-hidden">
            {course.title}
          </h3>
          <p className="text-sm text-gray-600 mt-2">by {course.instructor}</p>
          <div className="flex items-center mt-2">
            <span className="text-amber-500 font-bold mr-1">{course.rating}</span>
            {renderStars(course.rating)}
            <span className="text-xs text-gray-500 ml-1">({course.reviewCount})</span>
          </div>
          <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
            <span className="flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {course.examFocus}
            </span>
            <span className="flex items-center text-xs font-medium px-2 py-1 bg-gray-100 rounded-full">
              {course.difficulty}
            </span>
          </div>
          <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between items-center">
            <div>
              <span className="text-lg font-bold text-gray-800">
                ₹{course.discountPrice}
              </span>
              <span className="text-sm text-gray-500 line-through ml-2">
                ₹{course.price}
              </span>
              <span className="ml-2 text-green-600 text-xs font-bold">
                {discountPct}% OFF
              </span>
            </div>
            <Link to={`/coursePage/${course.id}`}
              className={`px-3 py-2 rounded-lg font-medium text-sm text-white bg-gradient-to-r ${gradient}`}
            >
              Enroll Now
            </Link>
          </div>
        </div>
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-5 transition-opacity duration-300 ${
            hover ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <h3 className="text-lg font-bold text-white">{course.title}</h3>
          <p className="text-white/80 text-sm my-2">
            Master problem-solving with {course.problems}+ carefully selected problems,
            video solutions, and practice tests.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="bg-white/20 rounded-lg p-2 text-center">
              <p className="text-white text-xs">Difficulty</p>
              <p className="text-white font-bold text-sm">{course.difficulty}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-2 text-center">
              <p className="text-white text-xs">Success Rate</p>
              <p className="text-white font-bold text-sm">92%</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Link to={`/coursePage/${course.id}`} className="flex-1 bg-white text-purple-800 py-2 rounded-lg font-medium text-sm">
              Sample Problems
            </Link>
            <Link to={`/coursePage/${course.id}`} className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-2 rounded-lg font-medium text-sm">
              Enroll Now
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // ─── Course Section ────────────────────────────────────────────
  const CourseSection: React.FC<{ categoryId: string; isActive?: boolean }> = ({
    categoryId,
    isActive = false,
  }) => {
    const name = categories.find((c) => c.id === categoryId)?.name;
    const courses = coursesByCategory[categoryId] || [];
    if (!name) return null;

    return (
      <section className={`w-full mb-12 ${isActive ? '' : 'pt-10 border-t'}`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`text-2xl font-bold ${isActive ? 'text-purple-800' : 'text-gray-800'}`}>
            {name}
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-sm">{courses.length} courses</span>
            <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
              Problem-Based
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 justify-center">
          {courses.map((course) => (
            <ProblemCourseCard key={course.id} course={course} />
          ))}
        </div>
        {isActive && (
          <div className="mt-6 text-center">
            <button
              onClick={() =>
                window.scrollTo({
                  top: document.getElementById('all-courses')!.offsetTop - 100,
                  behavior: 'smooth',
                })
              }
              className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-100 text-purple-800 font-medium text-sm hover:bg-purple-200 transition-colors"
            >
              View All Classes
            </button>
          </div>
        )}
      </section>
    );
  };

  // ─── Render ───────────────────────────────────────────────────
  const ordered = [
    activeCategory,
    ...categories.filter((c) => c.id !== activeCategory).map((c) => c.id),
  ];

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto mb-8">
        {/* <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-2">
          Class-Based Problem Solving Courses
        </h1> */}
        {/* <p className="text-center text-gray-600 mb-6">
          Master your academics through intensive practice and expert guidance
        </p> */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                setActiveCategory(cat.id);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`px-3 md:px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        <CourseSection categoryId={ordered[0]} isActive />
        <div id="all-courses" className="py-2">
          <h2 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-6">
            All Class Courses
          </h2>
        </div>
        {ordered.slice(1).map((id) => (
          <CourseSection key={id} categoryId={id} />
        ))}
      </div>
    </div>
  );
};

export default CoursesPage;
